import React, {forwardRef} from 'react'
import {resolveCertificateField} from './certificateRender'
import {CertificateCanvasDocument, CertificateRenderContext} from './certificateTypes'
import './CertificateCanvas.scss'

type Props = {
  document: CertificateCanvasDocument
  context: CertificateRenderContext
}

function resolveCertificateImageUrl(url: string): string {
  return url.replace(
    '/media/logos/Ad_Astra_Logo_Red_prod.png',
    '/media/logos/Ad _Astra_Logo_Red_prod.png'
  )
}

export const CertificateCanvasView = forwardRef<HTMLDivElement, Props>(
  function CertificateCanvasView({document: doc, context}, ref) {
    const sorted = [...doc.layers].sort((a, b) => a.z - b.z)
    const isClassic = (doc.theme ?? 'classic') === 'classic'
    const accent = doc.accentColor || '#a3a242'

    return (
      <div
        ref={ref}
        className={isClassic ? 'cert-canvas-page cert-canvas-page--classic' : 'cert-canvas-page'}
        style={{
          width: `${doc.width}px`,
          height: `${doc.height}px`,
          backgroundColor: doc.backgroundColor,
          backgroundImage: doc.backgroundImageUrl ? `url(${doc.backgroundImageUrl})` : undefined,
        }}
      >
        {isClassic ? (
          <div className='cert-adastra-frame-overlay' aria-hidden='true'>
            <div className='cert-adastra-inner-overlay' />
          </div>
        ) : null}

        {sorted.map((L) => {
          const baseStyle: React.CSSProperties = {
            width: `${L.w}px`,
            height: `${L.h}px`,
            transform: `translate(${L.x}px, ${L.y}px)`,
            zIndex: L.z,
          }

          if (L.kind === 'shape') {
            return (
              <div
                key={L.id}
                className='cert-canvas-layer cert-canvas-layer--shape'
                style={{
                  ...baseStyle,
                  backgroundColor: L.backgroundColor || accent,
                  borderRadius: L.borderRadius ? `${L.borderRadius}px` : undefined,
                  border: L.border,
                }}
              />
            )
          }

          if (L.kind === 'image' && L.imageUrl) {
            return (
              <div
                key={L.id}
                className='cert-canvas-layer cert-canvas-layer--image'
                style={baseStyle}
              >
                <img
                  className='cert-canvas-layer__image'
                  src={resolveCertificateImageUrl(L.imageUrl)}
                  alt=''
                  width={L.w}
                  height={L.h}
                />
              </div>
            )
          }

          const text =
            L.kind === 'field' && L.fieldKey
              ? resolveCertificateField(L.fieldKey, context)
              : L.text || ''
          const nameOrTitleLayer =
            L.kind === 'field' && (L.fieldKey === 'learnerName' || L.fieldKey === 'trainingTitle')

          return (
            <div
              key={L.id}
              className={`cert-canvas-layer cert-canvas-layer--text cert-canvas-layer--align-${
                L.textAlign ?? 'center'
              }`}
              style={{
                ...baseStyle,
                fontSize: `${L.fontSize ?? 16}px`,
                fontWeight: L.fontWeight ?? 'normal',
                fontStyle: L.fontStyle ?? 'normal',
                letterSpacing: L.letterSpacing ?? undefined,
                color: L.color ?? '#181c32',
                fontFamily:
                  L.fontFamily || (nameOrTitleLayer ? 'Arial, Helvetica, sans-serif' : undefined),
              }}
            >
              <span className='cert-canvas-layer__text'>{text}</span>
            </div>
          )
        })}
      </div>
    )
  }
)
